<template>
  <div>
    <dialog ref="dlgAtLocker">
      <form method="dialog" class="m-0">
        <p class="mb-3">{{ $t('locker.dialogs.atLocker.question') }}</p>
        <div class="d-flex gap-2 justify-content-end">
          <button class="btn btn-outline-secondary" value="no">{{ $t('common.no') }}</button>
          <button class="btn btn-success" value="yes">{{ $t('common.yes') }}</button>
        </div>
      </form>
    </dialog>

    <dialog ref="dlgChoice">
      <form method="dialog" class="m-0">
        <p class="mb-3">{{ $t('locker.dialogs.choice.title') }}</p>
        <div class="d-grid gap-2">
          <button class="btn btn-success" value="stay">{{ $t('locker.dialogs.choice.stay') }}</button>
          <button class="btn btn-warning" value="leave">{{ $t('locker.dialogs.choice.leave') }}</button>
          <button class="btn btn-outline-secondary" value="cancel">{{ $t('common.cancel') }}</button>
        </div>
      </form>
    </dialog>

    <dialog ref="dlgConfirmLeave">
      <form method="dialog" class="m-0">
        <p class="mb-3">{{ $t('locker.dialogs.confirmLeave.title') }}</p>
        <div class="d-flex gap-2 justify-content-end">
          <button class="btn btn-outline-secondary" value="no">{{ $t('common.no') }}</button>
          <button class="btn btn-success" value="yes">{{ $t('locker.dialogs.confirmLeave.confirm') }}</button>
        </div>
      </form>
    </dialog>

    <dialog ref="dlgUnlocked">
      <form method="dialog" class="m-0">
        <p class="mb-1">{{ $t('locker.dialogs.unlocked.title') }}</p>
        <p class="mb-3">{{ $t('locker.dialogs.unlocked.body') }}</p>
        <div class="d-flex gap-2 justify-content-end">
          <button class="btn btn-success" value="ok">{{ $t('common.ok') }}</button>
        </div>
      </form>
    </dialog>

  </div>
</template>

<script>
import LockerService from '@/services/LockerService';

export default {
  props: [
    'locker',
    'token',
    'eventBus',
  ],

  mounted() {
    this.eventBus.$on('openLocker', this.openLocker);
  },

  methods: {
    openLocker() {
      if (!this.locker || !this.locker.is_electronic_locker || !this.token) {
        return;
      }

      // 1. Question: are you at your locker?
      this.showDialog(this.$refs.dlgAtLocker, 'locker.dialogs.atLocker.question')
          .then((atLocker) => {
            if (atLocker !== 'yes') {
              return;
            }

            if (!this.locker.has_been_opened) {
              return 'proceed-stay'; // Skip to step 3.
            }

            // 2. Choice: stay / leave
            return this.showDialog(this.$refs.dlgChoice, 'locker.dialogs.choice.title');
          })
          .then((choice) => {
            if (!choice) {
              return;
            }

            if (choice === 'proceed-stay') {
              return choice;
            }

            if (choice === 'stay') {
              // User stays → Open instantly
              return 'proceed-stay';
            }

            if (choice === 'leave') {
              // User leaves → ask confirmation
              return this.showDialog(this.$refs.dlgConfirmLeave, 'locker.dialogs.confirmLeave.title')
                  .then((confirmedLeave) => (confirmedLeave === 'yes' ? 'proceed-leave' : null));
            }

            // Cancel / fallback
            return null;
          })
          .then((shouldProceed) => {
            if (!shouldProceed) {
              return;
            }

            if (shouldProceed === 'proceed-leave') {
              // 3. Open locker and leave + show success-dialog
              return this.openLockerAndLeaveRequest()
                  .then(() => this.showDialog(this.$refs.dlgUnlocked, 'locker.dialogs.unlocked.title'));
            }

            if (shouldProceed === 'proceed-stay') {
              // 3. Open locker + show success-dialog
              return this.openLockerRequest()
                  .then(() => this.showDialog(this.$refs.dlgUnlocked, 'locker.dialogs.unlocked.title'));
            }
          });
    },
    /**
     * Shows a <dialog> element and returns a Promise
     * resolving with the selected `returnValue`. Falls back to
     * window.confirm if <dialog> is not supported.
     */
    showDialog(dialogEl, fallbackI18nKey = 'common.confirm') {
      return new Promise((resolve) => {
        if (!dialogEl) {
          const res = window.confirm(this.$t(fallbackI18nKey));
          resolve(res ? 'yes' : 'no');
          return;
        }

        const handleClose = () => {
          resolve(dialogEl.returnValue);
        };

        dialogEl.addEventListener('close', handleClose, {once: true});

        if (typeof dialogEl.showModal === 'function') {
          dialogEl.showModal();
        } else if (typeof dialogEl.show === 'function') {
          dialogEl.show();
        } else {
          // Browser without <dialog>-support
          const res = window.confirm(
              dialogEl.textContent || this.$t(fallbackI18nKey),
          );
          resolve(res ? 'yes' : 'no');
        }
      });
    },

    /**
     * Execute the open locker command.
     * @returns {Promise<void>}
     */
    openLockerRequest() {
      return LockerService.open(this.token, this.locker.id).then(() => {
        this.locker.has_been_opened = true;
        return Promise.resolve();
      });
    },

    /**
     * Execute the open and leave locker command.
     * @returns {Promise<void>}
     */
    openLockerAndLeaveRequest() {
      return LockerService.openAndLeave(this.token, this.locker.id).then(() => {
        this.locker.has_been_opened = true;
        this.$set(this.locker, 'user_has_left', true);

        return Promise.resolve();
      });
    },
  },
};
</script>
